import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchSupplierInfo,
  saveSupplierInfo,
  saveSupplierInfoById,
  fetchCountries,
  createSupplierInfo,
} from '../../services/supplierService';
import { fetchUserDirectory } from '../../services/graphService';
import useUser from '../../auth/useUser';
import { useMsal } from '@azure/msal-react';

const STATUS_OPTIONS = ['Approved', 'Pending', 'Suspended'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fmtDateShort(d) {
  if (!d) return '\u2014';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '\u2014';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()}`;
}

/* ── Graph-backed person picker ── */
function PersonPicker({ label, value, onChange, users, loadingUsers }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const [dropPos, setDropPos] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const portal = document.getElementById('pp-portal-drop');
        if (portal && portal.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users.slice(0, 30);
    return users
      .filter(u =>
        u.displayName.toLowerCase().includes(q) ||
        u.userPrincipalName.toLowerCase().includes(q) ||
        (u.mail && u.mail.toLowerCase().includes(q))
      )
      .slice(0, 30);
  }, [users, query]);

  const dropdownEl = open && dropPos && createPortal(
    <div
      id="pp-portal-drop"
      className="ms-drop ms-drop-portal"
      style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width, maxHeight: 220, overflowY: 'auto' }}
    >
      <div
        className="ms-opt"
        style={{ color: 'var(--t3)', fontStyle: 'italic' }}
        onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
      >
        <span className="ms-opt-lbl">{'\u2715'} Clear</span>
      </div>
      {filtered.map(u => (
        <div
          key={u.id}
          className="ms-opt"
          onClick={() => { onChange(u.displayName); setOpen(false); setQuery(''); }}
        >
          <span className="ms-opt-lbl">
            <strong>{u.displayName}</strong>
            {u.mail && <span style={{ fontSize: 11, color: 'var(--t3)', marginLeft: 6 }}>{u.mail}</span>}
          </span>
        </div>
      ))}
      {!loadingUsers && filtered.length === 0 && (
        <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--t3)' }}>No matches</div>
      )}
      {loadingUsers && (
        <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--t3)' }}>Loading directory\u2026</div>
      )}
    </div>,
    document.body
  );

  return (
    <div ref={wrapRef}>
      <div className="sup-field-label">{label}</div>
      <input
        className="sup-input"
        value={open ? query : (value || '')}
        onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(value || ''); }}
        placeholder={loadingUsers ? 'Loading users\u2026' : 'Start typing\u2026'}
      />
      {dropdownEl}
    </div>
  );
}

/* ── Country picker (inline typeahead for contact location) ── */
function CountryInput({ value, onChange, countries }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const [dropPos, setDropPos] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 200) });
    };
    update();
    window.addEventListener('scroll', update, true);
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const portal = document.getElementById('ci-portal-drop');
        if (portal && portal.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = (open ? query : '').trim().toLowerCase();
    if (!q) return countries.slice(0, 20);
    return countries.filter(c => c.toLowerCase().includes(q)).slice(0, 20);
  }, [countries, query, open]);

  const portal = open && dropPos && createPortal(
    <div
      id="ci-portal-drop"
      className="ms-drop ms-drop-portal"
      style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width, maxHeight: 180, overflowY: 'auto', zIndex: 1100 }}
    >
      {filtered.map(c => (
        <div key={c} className="ms-opt" onClick={() => { onChange(c); setOpen(false); setQuery(''); }}>
          <span className="ms-opt-lbl">{c}</span>
        </div>
      ))}
      {filtered.length === 0 && (
        <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--t3)' }}>No matches</div>
      )}
    </div>,
    document.body
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="sup-input"
        value={open ? query : (value || '')}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(value || ''); }}
        placeholder="Start typing\u2026"
        style={{ fontSize: 12.5 }}
      />
      {portal}
    </div>
  );
}

/* ── Editable Contact Details modal ── */
function DetailsModal({ supplierId, supplierName, row, countries, onClose, onToast, onRowUpdated }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const splitField = (v) => {
    if (!v || String(v).trim() === '') return [];
    return String(v).split(/,\s*/).map(s => s.trim());
  };

  const buildContacts = useCallback(() => {
    const names = splitField(row?.full_name);
    const titles = splitField(row?.title_list);
    const phones = splitField(row?.phone_list);
    const emails = splitField(row?.email_list);
    const locations = splitField(row?.country_list);
    const maxLen = Math.max(names.length, titles.length, phones.length, emails.length, locations.length);
    const arr = [];
    for (let i = 0; i < maxLen; i++) {
      arr.push({
        name: names[i] || '',
        title: titles[i] || '',
        phone: phones[i] || '',
        email: emails[i] || '',
        location: locations[i] || '',
      });
    }
    return arr;
  }, [row]);

  const [contacts, setContacts] = useState(buildContacts);
  const [editIdx, setEditIdx] = useState(null); // index being edited, or -1 for new row
  const [editDraft, setEditDraft] = useState(null);
  const [emailError, setEmailError] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [emptyConfirm, setEmptyConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tooltip, setTooltip] = useState(null);

  // Sync contacts from row when row changes externally
  useEffect(() => { setContacts(buildContacts()); }, [buildContacts]);

  const isPhone = (v) => /^[+\d\s()-]+$/.test(v);
  const cell = (v) => v || <span style={{ color: 'var(--t3)' }}>{'\u2014'}</span>;

  const hasData = contacts.length > 0 || editIdx === -1;

  const joinField = (arr, key) => arr.map(c => c[key] || '').join(', ');

  const patchContacts = async (newContacts) => {
    setSaving(true);
    try {
      const payload = {
        full_name: joinField(newContacts, 'name'),
        title_list: joinField(newContacts, 'title'),
        phone_list: joinField(newContacts, 'phone'),
        email_list: joinField(newContacts, 'email'),
        country_list: joinField(newContacts, 'location'),
      };
      const updated = await saveSupplierInfoById(supplierId, payload, 'System');
      setContacts(newContacts);
      if (onRowUpdated && updated?.supplier) onRowUpdated(updated.supplier);
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (idx) => {
    if (editIdx !== null) {
      setTooltip('Save or cancel your current edit first.');
      setTimeout(() => setTooltip(null), 2000);
      return;
    }
    setEditIdx(idx);
    setEditDraft({ ...contacts[idx] });
    setEmailError(false);
  };

  const startAdd = () => {
    if (editIdx !== null) {
      setTooltip('Save or cancel your current edit first.');
      setTimeout(() => setTooltip(null), 2000);
      return;
    }
    setEditIdx(-1);
    setEditDraft({ name: '', title: '', phone: '', email: '', location: '' });
    setEmailError(false);
  };

  const cancelEdit = () => {
    setEditIdx(null);
    setEditDraft(null);
    setEmailError(false);
  };

  const saveEdit = async (skipEmptyCheck) => {
    const d = editDraft;
    const isEmpty = !d.name.trim() && !d.title.trim() && !d.phone.trim() && !d.email.trim() && !d.location.trim();

    // Email validation
    if (d.email.trim() && !EMAIL_RE.test(d.email.trim())) {
      setEmailError(true);
      return;
    }

    // New-row empty check
    if (editIdx === -1 && isEmpty && !skipEmptyCheck) {
      setEmptyConfirm(true);
      return;
    }
    if (editIdx === -1 && isEmpty && skipEmptyCheck) {
      cancelEdit();
      return;
    }

    let newContacts;
    if (editIdx === -1) {
      newContacts = [...contacts, { ...d }];
    } else {
      newContacts = contacts.map((c, i) => i === editIdx ? { ...d } : c);
    }

    const ok = await patchContacts(newContacts);
    if (ok) {
      onToast({ type: 'ok', msg: editIdx === -1 ? 'Contact added.' : 'Contact updated.' });
      setEditIdx(null);
      setEditDraft(null);
      setEmailError(false);
    } else {
      onToast({ type: 'error', msg: 'Failed to save contact.' });
    }
  };

  const doDelete = async (idx) => {
    const newContacts = contacts.filter((_, i) => i !== idx);
    const ok = await patchContacts(newContacts);
    if (ok) {
      onToast({ type: 'ok', msg: 'Contact removed.' });
      setDeleteConfirm(null);
      if (editIdx === idx) cancelEdit();
    } else {
      onToast({ type: 'error', msg: 'Could not remove contact.' });
      setDeleteConfirm(null);
    }
  };

  const setDraft = (k, v) => { setEditDraft(prev => ({ ...prev, [k]: v })); if (k === 'email') setEmailError(false); };

  const renderInput = (key, placeholder, type) => (
    <input
      className="sup-input"
      type={type || 'text'}
      value={editDraft[key]}
      onChange={(e) => setDraft(key, e.target.value)}
      placeholder={placeholder}
      style={{ fontSize: 12.5, padding: '4px 8px', ...(key === 'email' && emailError ? { borderColor: '#dc2626' } : {}) }}
    />
  );

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1080, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '90%', maxWidth: 960, maxHeight: '80vh', background: '#fff', borderRadius: 14, display: 'flex', flexDirection: 'column' }}
      >
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">Contact Details {'\u2014'} {supplierName}</h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>{'\u00d7'}</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', position: 'relative' }}>
          {tooltip && (
            <div style={{
              position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
              background: '#1f2937', color: '#fff', padding: '6px 14px', borderRadius: 6,
              fontSize: 12, zIndex: 10, whiteSpace: 'nowrap',
            }}>{tooltip}</div>
          )}

          {!hasData ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--t3)', fontSize: 14 }}>
              No contacts registered yet. Click Add Contact to add the first one.
            </div>
          ) : (
            <table className="sup-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Title</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Location</th>
                  <th style={{ width: 80, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((r, i) => {
                  if (editIdx === i) {
                    return (
                      <tr key={i}>
                        <td>{renderInput('name', 'Full name')}</td>
                        <td>{renderInput('title', 'Job title')}</td>
                        <td>{renderInput('phone', '+1 555-0100')}</td>
                        <td>
                          {renderInput('email', 'name@example.com', 'email')}
                          {emailError && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>Invalid email</div>}
                        </td>
                        <td><CountryInput value={editDraft.location} onChange={(v) => setDraft('location', v)} countries={countries} /></td>
                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button type="button" onClick={() => saveEdit(false)} disabled={saving} title="Save" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: 16, padding: '2px 4px' }}><i className="bi bi-check-lg"></i></button>
                          <button type="button" onClick={cancelEdit} disabled={saving} title="Cancel" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, padding: '2px 4px' }}><i className="bi bi-x-lg"></i></button>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={i}>
                      <td>{cell(r.name)}</td>
                      <td>{cell(r.title)}</td>
                      <td>
                        {r.phone ? (
                          isPhone(r.phone)
                            ? <a href={`tel:${r.phone.replace(/\s/g, '')}`} style={{ color: '#1d4ed8', textDecoration: 'none' }}>{r.phone}</a>
                            : r.phone
                        ) : <span style={{ color: 'var(--t3)' }}>{'\u2014'}</span>}
                      </td>
                      <td>
                        {r.email ? (
                          <a href={`mailto:${r.email}`} style={{ color: '#1d4ed8', textDecoration: 'none' }}>{r.email}</a>
                        ) : <span style={{ color: 'var(--t3)' }}>{'\u2014'}</span>}
                      </td>
                      <td>{cell(r.location)}</td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button type="button" onClick={() => startEdit(i)} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--teal)', fontSize: 14, padding: '2px 4px' }}><i className="bi bi-pencil"></i></button>
                        <button type="button" onClick={() => setDeleteConfirm(i)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, padding: '2px 4px' }}
                          onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; }}
                        ><i className="bi bi-trash3"></i></button>
                      </td>
                    </tr>
                  );
                })}
                {/* New-row at bottom */}
                {editIdx === -1 && (
                  <tr>
                    <td>{renderInput('name', 'Full name')}</td>
                    <td>{renderInput('title', 'Job title')}</td>
                    <td>{renderInput('phone', '+1 555-0100')}</td>
                    <td>
                      {renderInput('email', 'name@example.com', 'email')}
                      {emailError && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>Invalid email</div>}
                    </td>
                    <td><CountryInput value={editDraft.location} onChange={(v) => setDraft('location', v)} countries={countries} /></td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => saveEdit(false)} disabled={saving} title="Save" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: 16, padding: '2px 4px' }}><i className="bi bi-check-lg"></i></button>
                      <button type="button" onClick={cancelEdit} disabled={saving} title="Cancel" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, padding: '2px 4px' }}><i className="bi bi-x-lg"></i></button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="sup-modal-foot" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="sup-btn primary" onClick={startAdd} disabled={saving}>
            <i className="bi bi-plus-circle me-1"></i> Add Contact
          </button>
          <button type="button" className="sup-btn" onClick={onClose}>
            <i className="bi bi-x-circle me-1"></i> Close
          </button>
        </div>
      </div>

      {/* Delete confirmation */}
      {deleteConfirm !== null && (
        <div
          onClick={() => setDeleteConfirm(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1090, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div className="sup-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0">Remove contact?</h5>
              <button type="button" className="sup-modal-close" onClick={() => setDeleteConfirm(null)}>{'\u00d7'}</button>
            </div>
            <div className="sup-modal-body" style={{ fontSize: 14 }}>
              Remove contact {'\u2018'}<strong>{contacts[deleteConfirm]?.name || `#${deleteConfirm + 1}`}</strong>{'\u2019'}? This cannot be undone.
            </div>
            <div className="sup-modal-foot">
              <button type="button" className="sup-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button type="button" className="sup-btn primary" style={{ background: '#dc2626' }} onClick={() => doDelete(deleteConfirm)} disabled={saving}>
                {saving ? 'Removing\u2026' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty-block warning */}
      {emptyConfirm && (
        <div
          onClick={() => setEmptyConfirm(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1090, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div className="sup-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0">Empty contact</h5>
              <button type="button" className="sup-modal-close" onClick={() => setEmptyConfirm(false)}>{'\u00d7'}</button>
            </div>
            <div className="sup-modal-body" style={{ fontSize: 14 }}>
              This contact has all fields empty. Empty contacts will be skipped on save. Save anyway, or fill it in?
            </div>
            <div className="sup-modal-foot">
              <button type="button" className="sup-btn" onClick={() => setEmptyConfirm(false)}>Go Back</button>
              <button type="button" className="sup-btn primary" onClick={() => { setEmptyConfirm(false); saveEdit(true); }}>Save Anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main Supplier Info Modal ── */
export default function SupplierInfoModal({ supplierId, supplierName, mode = 'edit', onClose, onSaved, onToast }) {
  const { displayName: editorName } = useUser();
  const { instance: msalInstance } = useMsal();
  const isCreate = mode === 'create';
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);
  const [showCountryDrop, setShowCountryDrop] = useState(false);
  const countryWrapRef = useRef(null);
  const [showDetails, setShowDetails] = useState(false);
  const [supplierScore, setSupplierScore] = useState(null);
  const [scoreLoaded, setScoreLoaded] = useState(false);

  // Graph users for person picker
  const [graphUsers, setGraphUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const usersLoadedRef = useRef(false);

  const loadUsers = () => {
    if (usersLoadedRef.current) return;
    usersLoadedRef.current = true;
    setLoadingUsers(true);
    fetchUserDirectory(msalInstance)
      .then(u => setGraphUsers(u || []))
      .finally(() => setLoadingUsers(false));
  };

  const [form, setForm] = useState({
    supplier_name: isCreate ? '' : (supplierName || ''),
    supplier_approval_status: 'Pending',
    supplier_headquarter_country: '',
    account_manager: '',
    account_manager2: '',
    supplier_url: '',
    supplier_newsroom: '',
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cts = await fetchCountries().catch(() => []);
        if (alive) setCountries(cts);
        if (isCreate) return;
        const info = await fetchSupplierInfo(supplierName).catch(() => null);
        if (!alive) return;
        if (info) {
          setRow(info);
          setForm({
            supplier_name: info.supplier_name || supplierName,
            supplier_approval_status: info.supplier_approval_status || 'Pending',
            supplier_headquarter_country: info.supplier_headquarter_country || '',
            account_manager: info.account_manager || '',
            account_manager2: info.account_manager2 || '',
            supplier_url: info.supplier_url || '',
            supplier_newsroom: info.supplier_newsroom || '',
          });
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [supplierName, isCreate]);

  useEffect(() => {
    if (!showCountryDrop) return undefined;
    const handler = (e) => {
      if (countryWrapRef.current && !countryWrapRef.current.contains(e.target)) {
        setShowCountryDrop(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCountryDrop]);

  // Fetch supplier score
  useEffect(() => {
    if (isCreate || !supplierName) return;
    const API_SCOPE = 'api://6069b616-c7f2-4978-9f9c-180ec12f1844/access_as_user';
    (async () => {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return;
        let token;
        try { token = (await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account: accounts[0] })).accessToken; }
        catch { token = (await msalInstance.acquireTokenPopup({ scopes: [API_SCOPE] })).accessToken; }
        const r = await fetch(`${import.meta.env.VITE_API_URL}/api/supplier-scores/${encodeURIComponent(supplierName)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setSupplierScore(d.score?.total_score ?? null);
        }
      } catch {}
      finally { setScoreLoaded(true); }
    })();
  }, [isCreate, supplierName, msalInstance]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const filteredCountries = useMemo(() => {
    const q = String(form.supplier_headquarter_country || '').trim().toLowerCase();
    if (!q) return countries.slice(0, 30);
    return countries.filter(c => String(c).toLowerCase().includes(q)).slice(0, 30);
  }, [countries, form.supplier_headquarter_country]);

  const save = async () => {
    if (isCreate && !form.supplier_name.trim()) {
      onToast({ type: 'error', msg: 'Supplier Name is required' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      let updated;
      if (isCreate) {
        updated = await createSupplierInfo(payload, editorName);
        onToast({ type: 'ok', msg: 'New supplier added successfully' });
      } else if (supplierId != null && row?.supplier_id) {
        updated = await saveSupplierInfoById(row.supplier_id, payload, editorName);
        onToast({ type: 'ok', msg: 'Supplier information updated' });
      } else {
        updated = await saveSupplierInfo(supplierName, payload, editorName);
        onToast({ type: 'ok', msg: 'Supplier information updated' });
      }
      setRow(updated?.supplier || updated);
      if (onSaved) onSaved(updated?.supplier || updated);
      onClose();
    } catch (err) {
      onToast({
        type: 'error',
        msg: err?.response?.data?.error || (isCreate ? 'Failed to add supplier' : 'Failed to save supplier info'),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sup-modal-backdrop" onClick={onClose}>
        <div className="sup-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
          <div className="sup-modal-head">
            <h5 className="sup-modal-title mb-0">
              <span className="sup-title-main">
                {isCreate ? 'Add New Supplier' : 'Supplier Information'}
              </span>
              {!isCreate && <span className="sup-title-sub">{supplierName}{scoreLoaded && (
                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: supplierScore != null ? 'var(--teal)' : 'var(--t3)' }}>
                  {supplierScore != null ? `Score: ${supplierScore}` : 'Score: \u2014 (not scored)'}
                </span>
              )}</span>}
            </h5>
            <button type="button" className="sup-modal-close" onClick={onClose}>{'\u00d7'}</button>
          </div>

          <div className="sup-modal-body">
            {loading ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--t3)' }}>Loading\u2026</div>
            ) : (
              <div className="row g-3">
                {/* Editable fields */}
                <div className="col-12 col-md-6">
                  <div className="sup-field-label">Supplier Name</div>
                  <input
                    className="sup-input"
                    value={form.supplier_name}
                    onChange={(e) => set('supplier_name', e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <div className="sup-field-label">Supplier Status</div>
                  <select
                    className="sup-select"
                    value={form.supplier_approval_status}
                    onChange={(e) => set('supplier_approval_status', e.target.value)}
                  >
                    {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                <div className="col-12 col-md-6" ref={countryWrapRef} style={{ position: 'relative' }}>
                  <div className="sup-field-label">Supplier Head Quarter Country</div>
                  <input
                    className="sup-input"
                    value={form.supplier_headquarter_country}
                    onChange={(e) => { set('supplier_headquarter_country', e.target.value); setShowCountryDrop(true); }}
                    onFocus={() => setShowCountryDrop(true)}
                    placeholder="Start typing\u2026"
                  />
                  {showCountryDrop && filteredCountries.length > 0 && (
                    <div className="ms-drop" style={{ left: 12, right: 12, top: 'auto', marginTop: 4 }}>
                      {filteredCountries.map(c => (
                        <div
                          key={c}
                          className="ms-opt"
                          onClick={() => { set('supplier_headquarter_country', c); setShowCountryDrop(false); }}
                        >
                          <span className="ms-opt-lbl">{c}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-6" />

                {/* Internal FocusCare Person section */}
                <div className="col-12 col-md-6" onFocus={loadUsers}>
                  <PersonPicker
                    label="Primary Account Manager"
                    value={form.account_manager}
                    onChange={(v) => set('account_manager', v)}
                    users={graphUsers}
                    loadingUsers={loadingUsers}
                  />
                </div>
                <div className="col-12 col-md-6" onFocus={loadUsers}>
                  <PersonPicker
                    label="Backup Account Manager"
                    value={form.account_manager2}
                    onChange={(v) => set('account_manager2', v)}
                    users={graphUsers}
                    loadingUsers={loadingUsers}
                  />
                </div>

                {/* Supplier Web Site + Newsroom */}
                <div className="col-12 col-md-6">
                  <div className="sup-field-label">Supplier Web Site</div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input
                      className="sup-input"
                      value={form.supplier_url}
                      onChange={(e) => set('supplier_url', e.target.value)}
                      placeholder="https://example.com"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      disabled={!form.supplier_url.trim()}
                      onClick={() => {
                        let url = form.supplier_url.trim();
                        if (url && !/^https?:\/\//i.test(url)) url = 'https://' + url;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      title="Open in new tab"
                      style={{ background: 'none', border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 6px', cursor: form.supplier_url.trim() ? 'pointer' : 'not-allowed', color: form.supplier_url.trim() ? 'var(--teal)' : 'var(--t3)', flexShrink: 0 }}
                    >
                      <i className="bi bi-box-arrow-up-right" style={{ fontSize: 13 }}></i>
                    </button>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="sup-field-label">Supplier Newsroom</div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input
                      className="sup-input"
                      value={form.supplier_newsroom}
                      onChange={(e) => set('supplier_newsroom', e.target.value)}
                      placeholder="https://example.com/news"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      disabled={!form.supplier_newsroom.trim()}
                      onClick={() => {
                        let url = form.supplier_newsroom.trim();
                        if (url && !/^https?:\/\//i.test(url)) url = 'https://' + url;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      title="Open in new tab"
                      style={{ background: 'none', border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 6px', cursor: form.supplier_newsroom.trim() ? 'pointer' : 'not-allowed', color: form.supplier_newsroom.trim() ? 'var(--teal)' : 'var(--t3)', flexShrink: 0 }}
                    >
                      <i className="bi bi-box-arrow-up-right" style={{ fontSize: 13 }}></i>
                    </button>
                  </div>
                </div>

                {/* Read-only metadata row */}
                <div className="col-12 col-md-4">
                  <div className="sup-field-label">Supplier ID</div>
                  <input
                    className="sup-input"
                    value={row?.supplier_id ?? '\u2014'}
                    disabled
                    style={{ background: 'var(--surf2)', color: 'var(--t3)' }}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <div className="sup-field-label">Creation Date</div>
                  <input
                    className="sup-input"
                    value={fmtDateShort(row?.created_at)}
                    disabled
                    style={{ background: 'var(--surf2)', color: 'var(--t3)' }}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <div className="sup-field-label">Latest Update</div>
                  <input
                    className="sup-input"
                    value={row?.updated_at
                      ? `${fmtDateShort(row.updated_at)} \u2014 Updated by ${row.updated_by || '\u2014'}`
                      : '\u2014'}
                    disabled
                    style={{ background: 'var(--surf2)', color: 'var(--t3)' }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="sup-modal-foot" style={{ justifyContent: 'space-between' }}>
            <div>
              {!isCreate && (
                <button type="button" className="sup-btn" onClick={() => setShowDetails(true)} disabled={loading}>
                  <i className="bi bi-person-lines-fill me-1"></i> Details
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="sup-btn" onClick={onClose} disabled={saving}>
                <i className="bi bi-x-circle me-1"></i> Cancel
              </button>
              <button type="button" className="sup-btn primary" onClick={save} disabled={saving || loading}>
                <i className="bi bi-check-circle me-1"></i>
                {saving ? 'Saving\u2026' : (isCreate ? 'Create Supplier' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showDetails && (
        <DetailsModal
          supplierId={row?.supplier_id}
          supplierName={form.supplier_name || supplierName}
          row={row}
          countries={countries}
          onClose={() => setShowDetails(false)}
          onToast={onToast}
          onRowUpdated={(updated) => setRow(updated)}
        />
      )}
    </>
  );
}
